import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { getClass } from '../../utils/component-helpers';

import analyticService from '../../services/analytics';
import { authService } from '../../services/auth';
import Constants from '../../app.constants';
import NewRelicService from '../../services/newRelic';
import { session } from '../../services/session';

import NavHeader from './navigation/header/nav-header';
import NavSidebar from './navigation/sidebar/nav-sidebar';
import Routes from '../../routes';
import Snackbar from '../misc/snackbar';

import './main.scss';
import '../../styles/constants.scss';
import '../../styles/narrativ_icons.scss';
import '../misc/tooltip.scss';

class Main extends Component {
	constructor(props) {
		super(props);

		this.AnalyticService = analyticService;
		this.AuthService = authService;
		this.Constants = Constants;
		this.NewRelic = new NewRelicService(this.Constants.ENV);
		this.Session = session;

		this.unauthorizedMap = {
			'notLoggedIn': ['account', 'admin', 'merchant', 'product_match', 'publisher'],
			'product_match': ['publisher', 'merchant'],
			'merchant': ['admin', 'publisher', 'product_match'],
			'publisher': ['admin', 'merchant', 'product_match'],
		};

		/*
		 * Use Cases:
		 * Sets a 'static-route' class to the page-container
		 * Conditional for rendering NavHeader and NavSidebar components
		 * Passed as a prop to NavHeader and NavSidebar components
		 * Page renders are delayed until the Session is set unless it's a static route
		 * Used in conjunction with noFooterRoutes to render a footer on static routes
		 */
		 this.staticRoutes = [
			'/v2/article-search',
			'/v2/change-password',
			'/v2/create-user',
			'/v2/login',
			'/v2/privacy-policy',
			'/v2/publisher/terms-conditions',
			'/v2/reset-password-completion',
			'/v2/reset-password-request',
		];

		/*
		 * Use Cases:
		 * Used in conjunction with staticRoutes to render a footer on static routes
		 * Conditional for ignoring moveContentRight when side nav is open
		 */
		 this.noFooterRoutes = [
			'/v2/article-search',
			'/v2/create-user',
			'/v2/login',
			'/v2/privacy-policy',
			'/v2/publisher/terms-conditions',
			'/v2/reset-password-completion',
			'/v2/reset-password-request',
		];

		/*
		 * Use Cases:
		 * Pages that have been implemented in React, only available to admins
		 * Passed as a prop to NavSidebar
		 */
		 this.implementedPaths = [
			'/article-search/keyword',
			'/article-search/brand',
			'/article-search/product',
			'/campaigns/new',
			'/campaigns/optimize',
			'/new-recs/cart-explorer',
			'/collections/',
			'/collections/create-collection',
		];

		/*
		 * Use Cases:
		 * Conditional in isUnexposedRoute for exposed routes during navigation
		 * Passed as a prop to NavSidebar
		 */
		 this.exposedPaths = [
			//  Admins
			'/bid-center/auction-config-editor',
			'/bid-center/bulk-match',
			'/bid-center/merchant-overview',
			'/bid-center/merch-ed-overview',
			'/bid-center/publisher-overview',
			'/smartlinks/edit',
			'/smartlinks/editor',

			// Publishers
			'/megatables/merchants',
			'/megatables/products',
			'/megatables/smartlinks',
			'/new-recs/product-research',
			'/new-recs/rates',
			'/new-recs/search-trends',

			// Merchants
			'/desk',
			'/megatables/recommendations',
			'/megatables/campaign-performance',

			// Shared
			'/dashboard',
			'/megatables/articles',
			'/terms-conditions',
			'/v2', // We reach this path when logging out of the dashboard
		];

		/*
		 * Use Cases:
		 * Pages that don't require authentication
		 */
		 this.noAuthRoutes = [
			'/',
			'/v2/article-search',
			'/v2/change-password',
			'/v2/create-user',
			'/v2/login',
			'/v2/privacy-policy',
			'/v2/reset-password-completion',
			'/v2/reset-password-request',
		];

		/*
		 * Use Cases:
		 * Whitelist select paths to select orgs during pilot.
		 * Conditional in isUnauthorized() to prevent unauthorized navigation
		 * Used with whitelistOrgs in isUnauthorized()
		 */
		this.whitelistedPaths = [
			'/smartlinks/editor',
		];

		/*
		 * Use Cases:
		 * Whitelist select orgs to access new routes during pilot.
		 * Conditional in redirect() to prevent unauthorized navigation
		 * Passed as a prop to NavSidebar
		 * Used with exposedPaths in NavSidebar when creating nav menu
		 * 1105: Cup of Jo, 1054: QA Sister Ping, 1007: NYMag, 1071: PureWow, 1001-1141: Hearst
		 */
		this.whitelistedOrgs = [1105, 1054, 1007, 1071, 1001, 1002, 1090, 1097, 1098, 1125, 1129, 1137, 1140, 1141];

		this.AuthService.setWhitelist(this.whitelistedOrgs);

		this.breakPoint = 1180;
		this.mobileBreakpoint = 1000;
		this.toggleNavSidebar = this.toggleNavSidebar.bind(this);

		this.state = {
			currentUser: this.Session, // Empty session
			navSidebarCollapsed: false,
			isAdminView: null,
			isMobile: window.innerWidth <= this.mobileBreakpoint,
		};
	}

	componentDidMount() {
		window.addEventListener('resize', () => this.checkWindowWidth());
		this.loadHotjarScript();
		this.resumeFromLocalStorage();
		this.redirect();
		this.NewRelic.setUp();

		this.nextWindowEvent = this.state.navSidebarCollapsed ? 'reveal' : 'hide';
		// Hide side nav if screen is narrow
		const merchEdOverview = this.props.location.pathname &&
			this.props.location.pathname.startsWith('/v2/admin/bid-center/merch-ed-overview');

		if (this.narrowScreen() || merchEdOverview) this.toggleNavSidebar('closed');
	}

	loadHotjarScript() {
		if (['staging', 'release'].indexOf(this.Constants.ENV) === -1) return;

		const hotjar = `
			(function(h,o,t,j,a,r){
				h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
				h._hjSettings={hjid:1284107,hjsv:6};
				a=o.getElementsByTagName('head')[0];
				r=o.createElement('script');r.async=1;
				r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
				a.appendChild(r);
			})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
		`;

		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.innerText = hotjar;
		document.getElementsByTagName('head')[0].appendChild(script);
	}

	componentDidUpdate(prevProps) {
		/*
		 * The path changes when login succeeds. We have to refresh
		 * current user with the new session.
		 */
		if (
			prevProps.location.pathname === '/v2/login'
			&& this.props.location.pathname !== '/v2/login'
		) {
			this.resumeFromLocalStorage();
			this.redirect();
		}

		if (
			prevProps.location.pathname !== this.props.location.pathname &&
			this.props.location.pathname.startsWith('/v2/admin/bid-center/merch-ed-overview')
		) {
			this.toggleNavSidebar('closed');
		}
	}

	resumeFromLocalStorage() {
		// Load user session from localStorage
		const currentUser = window.localStorage.currentUser;
		if (currentUser === undefined) return;

		try {
			this.Session.create(JSON.parse(currentUser));
		} catch (e) {
			console.error(e);
		}

		this.setState({
			currentUser: this.Session,
			isAdminView: this.Session.isAdminView,
		});

		this.AnalyticService.setUp(this.Constants.ENV === 'release', this.Session);

		if (this.Session.authVersion !== this.Constants.AUTH_VERSION) {
			this.AuthService.logout();
		}
	}

	/* User role and permissions */
	redirect() {
		const originalPath = this.props.location.pathname;
		const role = this.userRole();

		// Admin can access everything
		if (!this.unauthorizedMap[role] || this.state.currentUser.isAdmin) return;

		if (this.isUnauthorized(role, originalPath)) {
			this.redirectUnauthorizedUser(role, originalPath);
		} else if (this.state.currentUser && role === 'publisher' &&
			!this.state.currentUser.termsAccepted) {
			// Redirect publishers who haven't accepted terms and conditions.
			this.redirectPubUser();
		} else if (this.noAuthRoutes.includes(originalPath)) {
			// This is so we can test v2 login w/o infinite redirect to v1
			return;
		} else if (this.isUnexposedRoute(originalPath) && originalPath !== '/v2') {
			/*
			 * If a non-admin tries to route to an unexposed v2 path,
			 * route them back to wooshspa app
			 */
			window.location = originalPath.replace('v2', '#');
		}
	}

	redirectPubUser() {
		/*
		 * If the user is the admin of the account,
		 * they'll be redirected to accept terms and conditions.
		 * Secondary users are not allowed to access logged-in routes at all.
		 */
		this.props.history.push('/v2/publisher/terms-conditions');
	}

	redirectUnauthorizedUser(role, originalPath) {
		if (role === 'notLoggedIn') {
			this.props.history.push({
				pathname: '/v2/login',
				search: `?redirect=${originalPath}`,
			});
		} else {
			/*
			 * Logged in users trying to access routes
			 * they are not allowed in get sent back to the homepage,
			 * where they're redirected to the correct path by Redirect
			 */
			this.props.history.push('/v2');
		}
	}

	userRole() {
		switch (this.state.currentUser.userRole) {
			case 'publishers':
				return 'publisher';
			case 'merchants':
				return 'merchant';
			case 'product_match':
				return 'product_match';
			case 'admin':
				return 'admin';
			default:
				return 'notLoggedIn';
		}
	}

	isAdmin() {
		const userRole = this.userRole();
		if (!userRole) return false;
		return userRole === 'admin' || userRole === 'product_match';
	}

	isUnauthorized(role, originalPath) {
		/*
		 * Redirect users attempting to access routes outside their respective role
		 * E.g. /v2/publisher/1016/dashboard => publisher
		 */
		const orgRoute = originalPath.split('/')[2];
		const unauthorizedOrgRoute = this.unauthorizedMap[role].indexOf(orgRoute) > -1;

		// Redirect product match users attempting to access non product match or smartlink management route
		const isProductMatchRoute = originalPath.startsWith('/v2/admin/product-match')
			|| originalPath.startsWith('/v2/admin/smartlinks');
		const productMatcher = role === 'product_match' && !isProductMatchRoute;

		/*
		 * Redirect users from non whitelisted orgs attempting to manually navigate
		 * to unauthorized routes.
		 * Regex gets the 4th match group:
		 *   /v2/publisher/1049/megatables/articles => /megatables/articles
		 *   /v2/publisher/account/trackers => /account/trackers
		 */
		const routeMatches = originalPath.match(/(publisher|merchant)(\/\d+)*(\/.+)/);
		const whitelistedPath = this.whitelistedPaths.includes(routeMatches && routeMatches[3]);
		const whitelistedOrg = this.whitelistedOrgs.includes(this.state.currentUser.orgId);
		const whitelisted = whitelistedPath && !whitelistedOrg;

		return unauthorizedOrgRoute || productMatcher || whitelisted;
	}

	isUnexposedRoute(originalPath) {
		/*
		 * Get the 4th match group:
		 *   /v2/publisher/1049/megatables/articles => /megatables/articles
		 *   /v2/publisher/account/trackers => /account/trackers
		 */
		const routeMatches = originalPath.match(/(publisher|merchant)(\/\d+)*(\/.+)/);
		// Temp product match override to allow netscribe access to smartlink management page
		const productMatchRouteMatches = originalPath.match(/(admin)(\/smartlinks)(\/\d+)*(\/.+)/);
		const productMatchRoute = productMatchRouteMatches && productMatchRouteMatches[2] + productMatchRouteMatches[4];
		const productMatchOverride = this.state.currentUser.userRole === 'product_match'
			&& this.exposedPaths.includes(productMatchRoute);

		return !this.state.currentUser.isAdmin && !window.localStorage.adminTests &&
			!this.exposedPaths.includes(routeMatches && routeMatches[3]) && !productMatchOverride;
	}

	invalidNavRoute() {
		let invalidRoute = true;
		const paths = ['/v2/admin', '/v2/publisher', '/v2/merchant'];

		if (paths.some((substring) => this.props.location.pathname.includes(substring)) &&
			this.props.location.pathname !== '/v2/publisher-faq' &&
			this.props.location.pathname !== '/v2/publisher/terms-conditions') {
			invalidRoute = false;
		}

		return invalidRoute;
	}

	/* Layout functions, organized alphabetically */
	checkWindowWidth() {
		if (this.narrowScreen() && this.nextWindowEvent === 'hide') {
			this.toggleNavSidebar('closed');
			this.nextWindowEvent = 'reveal';
		} else if (!this.narrowScreen() && this.nextWindowEvent === 'reveal') {
			this.toggleNavSidebar('open');
			this.nextWindowEvent = 'hide';
		}

		if (window.innerWidth <= this.mobileBreakpoint && !this.state.isMobile) {
			this.setState({isMobile: true});
		} else if (window.innerWidth > this.mobileBreakpoint && this.state.isMobile) {
			this.setState({isMobile: false});
		}
	}

	getContainerClass() {
		const path = this.props.location.pathname;
		return this.staticRoutes.includes(path) ? 'static-page' : '';
	}

	moveContentRight() {
		if (
			!this.state.currentUser
				|| this.noFooterRoutes.includes(this.props.location.pathname)
		) return false;

		return !this.state.navSidebarCollapsed && !this.invalidNavRoute();
	}

	narrowScreen() {
		return window.innerWidth < this.breakPoint;
	}

	toggleNavSidebar(state) {
		// state: open|closed
		const collapsed = state === 'closed' || !this.state.navSidebarCollapsed;
		this.setState({navSidebarCollapsed: collapsed});
	}

	hideNav(currentPath) {
		// Hide the nav bars on certain pages
		const staticRoute = this.staticRoutes.some((path) => currentPath.startsWith(path));
		const redirecting = ['/', '/v2'].includes(currentPath);
		const notLoggedIn = this.userRole() === 'notLoggedIn';

		return staticRoute || redirecting || notLoggedIn;
	}

	update() {
		/*
		 * Keep navbar open and update navbar items when re-logging in as admin viewing as pub
		 * except if returning to Overview v2 since the navbar be closed by default
		 */
		if (!(
			this.props.location.pathname &&
			this.props.location.pathname.startsWith('/v2/admin/bid-center/merch-ed-overview')
		)) {
			this.setState((prevState) => prevState);
		}
	}

	render() {
		const currentPath = this.props.location.pathname;
		/*
		 * Wait until Session becomes available before rendering
		 * any logged-in component.
		 */
		if (!this.staticRoutes.includes(currentPath) && !this.Session.userId) {
			return null;
		}

		const footer = this.staticRoutes.includes(currentPath)
			&& !this.noFooterRoutes.includes(currentPath) ? (
				<footer>
					<div className="credentials-links">
						<a href="https://narrativ.com/">About</a>
						<a href="/#/privacy-policy">Privacy</a>
					</div>
				</footer>
			) : null;
		const navHeader = !this.hideNav(currentPath) ? (
			<NavHeader
				currentUser={this.state.currentUser}
				isMobile={this.state.isMobile}
				userRole={this.userRole()}
				staticRoutes={this.staticRoutes}
				toggleNavSidebar={this.toggleNavSidebar}
				navSidebarCollapsed={this.state.navSidebarCollapsed}
				update={this.update.bind(this)}>
			</NavHeader>
		) : null;
		const navSidebar = !this.hideNav(currentPath) ? (
			<NavSidebar
				location={this.props.location}
				history={this.props.history}
				userRole={this.userRole()}
				isAdmin={this.state.currentUser.isAdmin}
				// Path info
				implementedPaths={this.implementedPaths}
				whitelistedPaths={this.whitelistedPaths}
				exposedPaths={this.exposedPaths}
				staticRoutes={this.staticRoutes}
				// Booleans for display
				toggleNavSidebar={this.toggleNavSidebar}
				navSidebarCollapsed={this.state.navSidebarCollapsed}
				narrowScreen={this.narrowScreen.bind(this)}
				isAdminView={this.state.isAdminView}
				toggleAdminView={() => this.setState({isAdminView: !this.state.isAdminView})}
				whitelistedOrgs={this.whitelistedOrgs}>
			</NavSidebar>
		) : null;

		return (
			<div className={this.getContainerClass()}>
				{navHeader}
				{navSidebar}

				<div
					className={getClass(
						'page-container',
						`${this.moveContentRight() ? 'move-right' : 'move-left'}`,
					)}>
					<Routes
						isAdminView={this.state.isAdminView}
						isMobile={this.state.isMobile}
						newRelic={this.NewRelic}
					/>
				</div>

				<div className="tooltip"></div>
				<Snackbar />
				{footer}
			</div>
		);
	}
}

export default withRouter(Main);
